import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, TextField, Button, Typography, Alert, 
  Grid, MenuItem, Chip, Stack
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { contractProcessingService, candidateService, positionService } from '../services/api';
import { useToast } from './Common/ToastProvider';

const CVUpload = () => {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState('');
  const [positions, setPositions] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    appliedPosition: '',
    appliedPositionId: '',
    yearsOfExperience: '',
    currentPosition: '',
    currentCompany: '',
    education: '',
    university: '',
    major: '',
    graduationYear: '',
    expectedSalary: '',
    noticePeriod: '',
    remoteWorkPreference: '',
    status: 'New',
    source: 'CV Upload'
  });

  useEffect(() => {
    loadPositions();
  }, []);

  const loadPositions = async () => {
    try {
      const positions = await positionService.getAll();
      setPositions(positions || []);
    } catch (error) {
      console.error('Error loading positions:', error);
      setPositions([]);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError('');
    
    // Create URL for PDF preview
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPdfUrl(url);
    }
  };

  const handleExtract = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await contractProcessingService.extractCVData(file);

      setFormData({
        ...formData,
        fullName: data.fullName || '',
        email: data.email || '',
        phone: data.phone || '',
        location: data.location || '',
        appliedPosition: data.appliedPosition || '',
        yearsOfExperience: data.yearsOfExperience || '',
        currentPosition: data.currentPosition || '',
        currentCompany: data.currentCompany || '',
        education: data.education || '',
        university: data.university || '',
        major: data.major || '',
        graduationYear: data.graduationYear || '',
        expectedSalary: data.expectedSalary || '',
        noticePeriod: data.noticePeriod || '',
        remoteWorkPreference: data.remoteWorkPreference || ''
      });
      
      setExtractedData(data);
    } catch (error) {
      setError('Error extracting CV data: ' + (error.response?.data || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveCandidate = async () => {
    try {
      const candidateData = {
        ...formData,
        appliedPositionId: formData.appliedPositionId ? parseInt(formData.appliedPositionId) : null,
        yearsOfExperience: formData.yearsOfExperience ? parseInt(formData.yearsOfExperience) : null,
        graduationYear: formData.graduationYear ? parseInt(formData.graduationYear) : null,
        expectedSalary: formData.expectedSalary ? parseFloat(formData.expectedSalary) : null
      };

      await candidateService.create(candidateData);
      toast('Candidate saved successfully!', { variant: 'success' });
      
      // Reset form
      setExtractedData(null);
      setFile(null);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        location: '',
        appliedPosition: '',
        appliedPositionId: '',
        yearsOfExperience: '',
        currentPosition: '',
        currentCompany: '',
        education: '',
        university: '',
        major: '',
        graduationYear: '',
        expectedSalary: '',
        noticePeriod: '',
        remoteWorkPreference: '',
        status: 'New',
        source: 'CV Upload'
      });
    } catch (error) {
      setError('Error saving candidate: ' + (error.response?.data || error.message));
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Compact Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          CV Upload & Processing
        </Typography>
        
        {/* Inline Upload Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <input
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            id="cv-file-upload"
            type="file"
            onChange={handleFileChange}
          />
          
          <label htmlFor="cv-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUpload />}
              size="small"
            >
              Choose File
            </Button>
          </label>
          
          {file && (
            <>
              <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {file.name}
              </Typography>
              <Button
                variant="contained"
                onClick={handleExtract}
                disabled={loading || !file}
                size="small"
              >
                {loading ? 'Processing...' : 'Extract'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Candidate Form */}
      {extractedData && (
        <Box>
          {/* PDF Viewer - Full Width with More Height */}
          <Paper sx={{ p: 1, mb: 2, height: '70vh' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ px: 1 }}>
              CV Document
            </Typography>
            {pdfUrl && file?.type === 'application/pdf' ? (
              <iframe
                src={pdfUrl}
                width="100%"
                height="95%"
                style={{ border: 'none' }}
                title="CV Preview"
              />
            ) : pdfUrl && file?.type.startsWith('image/') ? (
              <img
                src={pdfUrl}
                alt="CV Preview"
                style={{ 
                  width: '100%', 
                  height: '95%', 
                  objectFit: 'contain' 
                }}
              />
            ) : (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No preview available
              </Typography>
            )}
          </Paper>

          {/* Compact Fixed Form at Bottom */}
          <Paper 
            sx={{ 
              p: 2, 
              position: 'sticky', 
              bottom: 0, 
              zIndex: 1000,
              backgroundColor: 'white',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
              maxHeight: '300px',
              overflowY: 'auto'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Candidate Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveCandidate}
                  size="small"
                >
                  Save Candidate
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => {
                    setExtractedData(null);
                    setFile(null);
                    setPdfUrl(null);
                  }}
                  size="small"
                >
                  Cancel
                </Button>
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Applied Position"
                  name="appliedPosition"
                  value={formData.appliedPosition}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Position (from system)"
                  name="appliedPositionId"
                  value={formData.appliedPositionId}
                  onChange={handleChange}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select Position</em>
                  </MenuItem>
                  {positions.map((position) => (
                    <MenuItem key={position.id} value={position.id}>
                      {position.title}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Years of Experience"
                  name="yearsOfExperience"
                  type="number"
                  value={formData.yearsOfExperience}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Current Position"
                  name="currentPosition"
                  value={formData.currentPosition}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Current Company"
                  name="currentCompany"
                  value={formData.currentCompany}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Expected Salary"
                  name="expectedSalary"
                  type="number"
                  value={formData.expectedSalary}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  label="Notice Period"
                  name="noticePeriod"
                  value={formData.noticePeriod}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Remote Work Preference"
                  name="remoteWorkPreference"
                  value={formData.remoteWorkPreference}
                  onChange={handleChange}
                  size="small"
                >
                  <MenuItem value="">Select Preference</MenuItem>
                  <MenuItem value="Remote">Remote</MenuItem>
                  <MenuItem value="Hybrid">Hybrid</MenuItem>
                  <MenuItem value="On-site">On-site</MenuItem>
                  <MenuItem value="Flexible">Flexible</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default CVUpload;





